import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useRouter, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { AgendaHandle, NavigationKernel, StoredAgendaConfig } from '@/shared/navigation/core';
import { createTanStackRouterAdapter } from '@/shared/navigation/core';
import { useNavigationContext } from '@/shared/navigation/react/useNavigationContext';
import { NavigationShellFrame } from '@/shared/navigation/shell';
import { PhoenixDesktop, PhoenixTaskbar } from '@/shared/windowing';
import { ModeSwitcher, SchemeSwitcher } from '@/shared/ui/theme';
import {
  LanguageSwitcher,
  MainMenu,
  MenuSearch,
  toast,
  type MenuActionContext,
  type MenuSearchSpecial,
  type MenuSearchHandle,
} from '@/shared/ui';
import {
  useMagicMenu,
  toMenuViewModel,
  sendMagicMenuItemClick,
  sendMagicFormEdit,
  setPendingMenuOpen,
  isPhoenixOnTopMode,
  useMagicDesktopStore,
  MAGIC_TO_PHOENIX_ACTIONS,
  type MenuOpenMode,
} from '@/shared/legacyMagicBridge';
import { getBusinessCaseByNumber } from '@/api/generated/reservations/reservations';
import { useAuth, userHasPermission, withPermissionGate } from '@/shared/auth';
import {
  recordAgendaHistory,
  linkLayerToHistory,
  type AgendaHistoryItem,
} from '@/shared/lib/agendaHistory';
import { legacyFormAgenda } from '@/modules/legacy-form/legacyFormAgenda';
import { WindowOnboarding } from '@/modules/window-onboarding';
import { DevelopmentDatabaseBadge } from './DevelopmentDatabaseBadge';
import { AgendaHistoryButton } from './AgendaHistoryButton';
import { DesktopHeaderControls } from './DesktopHeaderControls';
import { getMainMenuItems } from '../router/routeContracts';
import type { StandardMenuItem } from './mainMenuItems';
import { mergeNativeIntoSections, type NativeMenuEntry } from './mergeNativeIntoSections';

export interface AgendaRegistration {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic agenda handle, type params not needed for registration.
  agenda: AgendaHandle<any, any>;
  component: React.ComponentType<{ layerId: string }>;
  /**
   * Oprávnění vyžadované pro zobrazení agendy. S `permission` se komponenta obalí
   * do {@link PermissionGate} — bez oprávnění (i při vstupu přes URL/historii) se
   * místo obsahu zobrazí jednotná hláška „Přístup odepřen".
   */
  permission?: string;
}

export interface MenuNavigationShellPageProps {
  agendas: AgendaRegistration[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic agenda handle used only for openRoot identity.
  rootAgenda?: AgendaHandle<any, any>;
}

function registerAgendas(kernel: NavigationKernel, agendas: AgendaRegistration[]): void {
  const registry = kernel.getRegistry();

  for (const { agenda, component, permission } of agendas) {
    if (!registry.has(agenda.id)) {
      kernel.registerAgenda(agenda.config as StoredAgendaConfig);
    }
    kernel.registerComponent(
      agenda.id,
      permission ? withPermissionGate(permission, component) : component
    );
  }
}

function isPathActive(currentPath: string, itemPath: string): boolean {
  if (itemPath === '/') return currentPath === '/';
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
}

export function MenuNavigationShellPage({ agendas, rootAgenda }: MenuNavigationShellPageProps) {
  const { kernel } = useNavigationContext();
  const router = useRouter();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const searchRef = useRef<MenuSearchHandle>(null);
  const magicVersion = useMagicDesktopStore((s) => s.userInfo?.magicVersion);
  // Z Magic verze („57test.0 (db: Venua)") vezmeme jen číslo verze, bez „(db: …)" přívěšku.
  const magicVersionShort = magicVersion?.replace(/\s*\(.*$/, '').trim();
  const versionLabel = magicVersionShort
    ? `v${__APP_VERSION__} · v${magicVersionShort}`
    : `v${__APP_VERSION__}`;

  const baselineMenuItems = useMemo(() => getMainMenuItems(t), [t]);
  const { items: magicItems, isReady, loadError } = useMagicMenu();
  const phoenixOnTop = isPhoenixOnTopMode();
  const auth = useAuth();

  // Položky vázané na oprávnění (`permission`) zobraz jen tehdy, když ho přihlášený uživatel má
  // (config super-user vidí vše). Dokud není autentizace dokončená, gated položky skryj
  // (bezpečný default bez problikávání).
  const user = auth.status === 'authenticated' ? auth.user : null;
  const visibleMenuItems = useMemo(
    () =>
      baselineMenuItems.filter(
        (i) =>
          i.kind !== 'standard' ||
          i.permission === undefined ||
          userHasPermission(user, i.permission)
      ),
    [baselineMenuItems, user]
  );

  // Native položky s cílovou sekcí → merge do sekcí; bez sekce → samostatná tlačítka vlevo.
  const nativeEntries = useMemo<NativeMenuEntry[]>(
    () =>
      visibleMenuItems
        .filter((i): i is StandardMenuItem => i.kind === 'standard' && i.section !== undefined)
        .map((i) => ({
          id: i.path,
          title: i.label,
          section: i.section as string,
          group: i.group,
          key: i.key,
        })),
    [visibleMenuItems]
  );
  const leadingStandard = useMemo(
    () =>
      visibleMenuItems.filter(
        (i): i is StandardMenuItem => i.kind === 'standard' && i.section === undefined
      ),
    [visibleMenuItems]
  );

  const sections = useMemo(() => {
    const legacy = phoenixOnTop ? toMenuViewModel(magicItems) : [];
    return mergeNativeIntoSections(legacy, nativeEntries);
  }, [phoenixOnTop, magicItems, nativeEntries]);

  const loading = phoenixOnTop && !isReady && !loadError;
  const menuLoadError = phoenixOnTop && loadError;

  const handleMenuNavigate = useCallback(
    (path: string) => {
      if (window.location.pathname === path) {
        const rootLayer = kernel.getState().layers.find((l) => l.presentation === 'root');
        if (rootLayer) {
          kernel.openRoot({ id: rootLayer.agendaId } as Parameters<typeof kernel.openRoot>[0], {});
          return;
        }
      }
      void navigate({ to: path });
    },
    [kernel, navigate]
  );

  // Otevření položky menu. mode 'replace' = NAHRADIT komplet plochu (klik na položku),
  // mode 'detached' = otevřít v SAMOSTATNÉM (plovoucím) okně (čudlík).
  // Native → route navigace (přepíše plochu; plovoucí okno z menu zatím neumí → fallback na replace);
  // legacy → záměr + dispatch do Magicu (bridge pak udělá openRoot + zapíše do historie).
  const handleOpen = useCallback(
    (ctx: MenuActionContext, mode: 'replace' | 'detached') => {
      const node = ctx.node;
      // Externí WWW odkaz → otevři v nové záložce PŘÍMO (synchronně v user-gestu, jinak by popup
      // blocker zablokoval). Žádný dispatch do Magicu, žádný záměr, do historie nepatří.
      if (node.url) {
        window.open(node.url, '_blank', 'noopener');
        return;
      }
      if (node.origin === 'native') {
        recordAgendaHistory({ kind: 'native', target: node.id, title: node.title });
        handleMenuNavigate(node.id);
      } else {
        setPendingMenuOpen(mode, node.title);
        sendMagicMenuItemClick(node.id);
      }
    },
    [handleMenuNavigate]
  );

  // Našeptávač: otevření detailu obchodního případu dle čísla. Číslo → ID přes API; když existuje,
  // otevři legacy detail (ObjectCategory BusinessCase) přes `form/edit` (Magic probublá `form/open`,
  // bridge respektuje pending mode). Když ne, jen toast „nenalezen".
  const openBusinessCaseByNumber = useCallback(
    async (num: number, mode: MenuOpenMode) => {
      try {
        const res = await getBusinessCaseByNumber(num);
        if (res?.id != null) {
          setPendingMenuOpen(mode, t('businessCase.title', { number: num }));
          sendMagicFormEdit('BusinessCase', res.id);
        } else {
          toast({ severity: 'error', description: t('businessCase.notFound', { number: num }) });
        }
      } catch {
        toast({ severity: 'error', description: t('businessCase.notFound', { number: num }) });
      }
    },
    [t]
  );

  // Speciální výsledek našeptávače: čistě číselný dotaz → nabídka „Otevřít obchodní případ č. X" (první).
  const menuSearchSpecials = useCallback(
    (query: string): MenuSearchSpecial[] => {
      const trimmed = query.trim();
      if (!/^\d+$/.test(trimmed)) return [];
      const num = Number(trimmed);
      if (!Number.isSafeInteger(num)) return [];
      return [
        {
          id: `bc:${trimmed}`,
          title: t('businessCase.openByNumber', { number: trimmed }),
          icon: 'list',
          onActivate: (mode) => void openBusinessCaseByNumber(num, mode),
        },
      ];
    },
    [t, openBusinessCaseByNumber]
  );

  // Historie: znovuotevření PŘÍMO (legacy přes GlobalName — menuItemId je už po reloadu neplatné,
  // nejdeme přes Magic round-trip). mode 'replace' = nahradit plochu (klik), 'detached' = samostatné okno (čudlík).
  const historyOpen = useCallback(
    (item: AgendaHistoryItem, mode: 'replace' | 'detached') => {
      const id = recordAgendaHistory(item); // move-to-top
      if (item.kind === 'native') {
        handleMenuNavigate(item.target);
        return;
      }
      const params = { formGlobalName: item.target, formParams: item.params };
      const opened =
        mode === 'detached'
          ? kernel.openModal(legacyFormAgenda, params)
          : kernel.openRoot(legacyFormAgenda, params);
      void opened.then(() => {
        const newId = kernel.getState().activeTopLayerId;
        if (newId) {
          if (mode === 'detached') {
            // Fresh stackId → projekce zobrazí okno jako floating (PhoenixDesktop naseeduje rect).
            kernel.setLayerStackId(newId, `s_${crypto.randomUUID()}`);
          }
          linkLayerToHistory(newId, id);
        }
      });
    },
    [kernel, handleMenuNavigate]
  );

  const leadingItems = leadingStandard.map((item) => (
    <button
      key={item.id}
      type="button"
      className="legacy-menu__item"
      data-testid={`menu-item-${item.id}`}
      data-active={isPathActive(location.pathname, item.path) ? 'true' : undefined}
      onClick={() => handleMenuNavigate(item.path)}
    >
      {item.label}
    </button>
  ));

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Router adapter works against the stable TanStack runtime surface.
    const adapter = createTanStackRouterAdapter(router as any);
    kernel.connectRouter(adapter);
  }, [kernel, router]);

  useEffect(() => {
    registerAgendas(kernel, agendas);
  }, [kernel, agendas]);

  useEffect(() => {
    kernel.restoreFromCurrentUrl();

    // Bez rootAgendy (prázdná plocha na '/') se žádná agenda automaticky neotevírá –
    // deep-linky z URL zůstávají obnovené přes restoreFromCurrentUrl().
    if (rootAgenda && kernel.getState().layers.length === 0) {
      kernel.openRoot(rootAgenda, {});
    }
  }, [kernel, rootAgenda]);

  // Globální zkratka Alt+Q → focus do našeptávače agend. Detekce přes fyzickou klávesu
  // (`e.code === 'KeyQ'`, nezávislé na layoutu i OS — funguje i jako Option+Q v Safari/macOS),
  // `!ctrlKey` vyloučí AltGr (Win), `!metaKey` vyloučí Cmd. Z legacy oken (cross-origin Magic iframe)
  // přichází focus přes postMessage `desktop/focus-search` (Magic ho pošle z keydown handleru).
  useEffect(() => {
    const focusSearch = () => searchRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyQ') {
        e.preventDefault();
        focusSearch();
      }
    };
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { action?: string } | null;
      if (data?.action === MAGIC_TO_PHOENIX_ACTIONS.desktopFocusSearch) focusSearch();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      window.removeEventListener('message', onMessage);
    };
  }, []);

  return (
    <NavigationShellFrame
      mainMenu={
        <MainMenu
          sections={sections}
          loading={loading}
          loadError={menuLoadError}
          onOpen={(ctx) => handleOpen(ctx, 'replace')}
          onDetach={(ctx) => handleOpen(ctx, 'detached')}
          logo={
            <>
              Magic<span className="legacy-menu__logo-strong">2G</span>
            </>
          }
          leadingItems={leadingItems}
          rightContent={
            <div className="gap-phx-3 flex items-center">
              <MenuSearch
                ref={searchRef}
                sections={sections}
                onOpen={handleOpen}
                specialResults={menuSearchSpecials}
              />
              <LanguageSwitcher />
              <SchemeSwitcher />
              <ModeSwitcher />
              <DesktopHeaderControls />
            </div>
          }
        />
      }
      footer={
        <PhoenixTaskbar
          buildLabel={versionLabel}
          historySlot={
            <AgendaHistoryButton
              onOpen={(item) => historyOpen(item, 'replace')}
              onDetach={(item) => historyOpen(item, 'detached')}
            />
          }
          infoSlot={<DevelopmentDatabaseBadge />}
        />
      }
    >
      <PhoenixDesktop />
      <WindowOnboarding />
    </NavigationShellFrame>
  );
}
